import { DelegateProvider } from '../../interfaces/delegate-provider.interface';
import { LoggerService } from '../logger.service';
import { FunctionPropertyNames } from 'app-core/src/types';
import { IframeCommunicationDelegate } from '../../interfaces/iframe-communication.interface';

// -----------------------------------------------------------------------------
const kMessageToDelegate = new Map<
  string,
  FunctionPropertyNames<IframeCommunicationDelegate>
>([
  ['DirectInstallApplication', 'onDirectInstallApplicationRequested'],
  ['OpenApplicationInStore', 'onOpenApplicationInStoreRequested'],
  ['CloseNotification', 'onCloseNotificationRequested'],
  ['CloseNotificationAfterDownload', 'onCloseNotificationAfterDownload'],
  ['OpenNotificationSettings', 'onOpenNotificationSettingsRequested'],
  ['TurnOffNotifications', 'onTurnOffNotificationsRequested'],
  ['SendTracking', 'onSendTrackingRequested'],
  ['ToggleTimeoutTimer', 'onToggleTimeoutTimerRequested'],
  ['RecommendationInView', 'onRecommendationInView'],
  ['GameExcluded', 'onGameExcluded'],
  ['ApplicationExcluded', 'onApplicationExcluded'],
  ['OpenExternalUrl', 'onOpenExternalUrl'],
  ['NotificationABTest', 'OnNotificationABTest'],
  ['DownloadExternalNotification', 'OnDownloadExternalApplication'],
  ['CancelDownloadExternal', 'OnCancelDownloadExternal'],
  ['CloseWhileDownloadExternal', 'OnCloseWhileDownloadExternal'],
  ['CloseOnUIError', 'OnCloseOnUIError'],
]);

// -----------------------------------------------------------------------------
export class IframeCommunicationServiceImpl
  implements DelegateProvider<IframeCommunicationDelegate>
{
  private readonly logger = LoggerService.getCategory(
    'IframeCommunicationService',
  );

  private readonly listeners: Set<IframeCommunicationDelegate>;
  private activeIframe: HTMLIFrameElement;

  // ---------------------------------------------------------------------------
  public constructor() {
    this.listeners = new Set<IframeCommunicationDelegate>();
    this.registerPostMessageEvents();
  }

  // ---------------------------------------------------------------------------
  public registerIframe(iframe: HTMLIFrameElement) {
    if (this.activeIframe) {
      this.logger.warn('Iframe already registered, skipping');
      return;
    }

    this.logger.info('Registering Iframe');
    this.activeIframe = iframe;
  }

  // ---------------------------------------------------------------------------
  public deregisterIframe() {
    if (!this.activeIframe) {
      this.logger.warn('no iframe is registered');
      return;
    }

    this.activeIframe = null;
  }

  // ---------------------------------------------------------------------------
  public sendIframeMessage(message: any) {
    if (!this.activeIframe) {
      this.logger.warn(
        'Received Iframe message but no active iframe defined,' +
          ` message: ${message}`,
      );
      return;
    }

    try {
      this.activeIframe.contentWindow.postMessage(message, '*');
    } catch (error) {
      this.logger.error(`Error sending Iframe message message: ${message}`);
    }
  }

  // ---------------------------------------------------------------------------
  public triggerListenerEvent(
    eventName: FunctionPropertyNames<IframeCommunicationDelegate>,
    param: object,
  ) {
    try {
      if (!eventName) {
        return;
      }

      for (const listener of this.listeners) {
        if (!listener[eventName]) {
          continue;
        }

        listener[eventName](param);
      }
    } catch (error) {
      this.logger.error('Error triggering listener event', error);
    }
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IframeCommunicationDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IframeCommunicationDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  private registerPostMessageEvents() {
    window.addEventListener('message', this.handleMessageEvent.bind(this));
  }

  // ---------------------------------------------------------------------------
  private handleMessageEvent(event: MessageEvent) {
    try {
      const [eventName, eventParam] = this.getMessageInfo(event);
      if (!eventName) {
        return;
      }

      this.triggerListenerEvent(eventName, eventParam);
    } catch (error) {
      this.logger.error('Error handleMessageEvent', error);
    }
  }

  // ---------------------------------------------------------------------------
  private getMessageInfo(
    event: MessageEvent,
  ): [FunctionPropertyNames<IframeCommunicationDelegate>, object] {
    try {
      const { data } = event;
      if (!kMessageToDelegate.has(data.action)) {
        return [null, null];
      }

      const param = kMessageToDelegate.get(data.action);
      return [param, data.data];
    } catch (error) {
      this.logger.error('Error getMessageInfo', error);
      return [null, null];
    }
  }
}
